import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { GridColDef } from '@mui/x-data-grid-premium';
import {
  ConditionalTemplateQuestionSet_ConditionalTemplateQuestionSetDataGridPageFilter,
  ConditionalTemplateQuestionSet_FieldsFragment,
  ConditionalTemplateQuestionSetDataGridColumns,
  useConditionalTemplateQuestionSet_GetAllConditionalTemplateQuestionSetsForConditionalTemplateQuestionSetDataGridLazyQuery,
} from 'queries';
import { ChevronRight } from 'ts-react-feather-icons';

import { ServerEnabledDataGrid } from 'common/server-enabled-data-grid';
import HomeLayout from 'layouts/home-layout';
import { GridStateKey } from 'utils/grid-state';
import { defaultPaginationModel } from 'utils/pagination-model';

import ConditionalTemplateQuestionSetActions from './components/conditional-template-question-set-actions';
import CreateConditionalTemplateQuestionSetModal from './components/create-conditional-template-question-set-modal';
import EditConditionalTemplateQuestionSetModal from './components/edit-conditional-template-question-set-modal';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  button: { marginBottom: '10px' },
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  title: {
    fontWeight: 600,
  },
  routeIconContainer: {
    verticalAlign: 'middle',
  },
  link: {
    cursor: 'pointer',
  },
  header: {
    marginRight: '10px',
  },
}));

export function ManageConditionalTemplateQuestionSetsPage() {
  const classes = useStyles();
  const history = useHistory();

  const [selectedConditionalTemplateQuestionSet, setSelectedConditionalTemplateQuestionSet] =
    useState<ConditionalTemplateQuestionSet_FieldsFragment | undefined>(undefined);

  const [
    showCreateConditionalTemplateQuestionSetModal,
    setShowCreateConditionalTemplateQuestionSetModal,
  ] = useState<boolean>(false);

  const [
    showEditConditionalTemplateQuestionSetModal,
    setShowEditConditionalTemplateQuestionSetModal,
  ] = useState<boolean>(false);

  const [
    getAllConditionalTemplateQuestionSetsForConditionalTemplateQuestionSetDataGridLazyQuery,
    getAllConditionalTemplateQuestionSetsForConditionalTemplateQuestionSetDataGridLazyQueryResult,
  ] =
    useConditionalTemplateQuestionSet_GetAllConditionalTemplateQuestionSetsForConditionalTemplateQuestionSetDataGridLazyQuery(
      {
        variables: {
          input: {
            paginationInput: {
              page: defaultPaginationModel.page,
              pageSize: defaultPaginationModel.pageSize,
            },
          },
        },
        fetchPolicy: 'network-only',
      },
    );

  const columns: GridColDef<ConditionalTemplateQuestionSet_FieldsFragment>[] = [
    {
      disableColumnMenu: true,
      field: ConditionalTemplateQuestionSetDataGridColumns.Name,
      filterable: false,
      flex: 2,
      headerName: 'Name',
      renderCell: ({ row }) => {
        return row.name;
      },
    },
    {
      disableColumnMenu: true,
      field: ConditionalTemplateQuestionSetDataGridColumns.Questions,
      filterable: false,
      flex: 2,
      headerName: 'Questions',
      renderCell: ({ row }) => {
        return (
          <Button
            onClick={() => history.push(`/home/${row.id}/manage-conditional-template-questions`)}
          >
            Edit Questions ({row.conditionalTemplateQuestions?.length || 0})
          </Button>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: 'actions',
      filterable: false,
      flex: 0.5,
      headerName: 'Actions',
      renderCell: ({ row }) => {
        return (
          <ConditionalTemplateQuestionSetActions
            conditionalTemplateQuestionSet={row}
            onEditSelectedConditionalTemplateQuestionSet={() => {
              setSelectedConditionalTemplateQuestionSet(row);
              setShowEditConditionalTemplateQuestionSetModal(true);
            }}
          />
        );
      },
    },
  ];

  return (
    <HomeLayout showPortalName={false}>
      <>
        {showEditConditionalTemplateQuestionSetModal && selectedConditionalTemplateQuestionSet && (
          <EditConditionalTemplateQuestionSetModal
            showModal={showEditConditionalTemplateQuestionSetModal}
            setShowModal={setShowEditConditionalTemplateQuestionSetModal}
            setSelectedConditionalTemplateQuestionSet={setSelectedConditionalTemplateQuestionSet}
            selectedConditionalTemplateQuestionSet={selectedConditionalTemplateQuestionSet}
          />
        )}
        {showCreateConditionalTemplateQuestionSetModal && (
          <CreateConditionalTemplateQuestionSetModal
            showModal={showCreateConditionalTemplateQuestionSetModal}
            setShowModal={setShowCreateConditionalTemplateQuestionSetModal}
          />
        )}
        <div className={classes.container}>
          <div style={{ display: 'flex', verticalAlign: 'bottom' }}>
            <Typography className={classes.title} variant="h5" style={{ flex: 2 }}>
              <span
                onClick={() => {
                  history.push(`/home`);
                }}
              >
                <span className={classes.routeIconContainer}>
                  <img src="/home-icon.svg" alt="home-icon" />
                </span>
                <span className={classes.routeIconContainer}>
                  <ChevronRight />
                </span>
              </span>
              Conditional Question Sets
            </Typography>
          </div>
          <div className={classes.button}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ marginTop: '3px', padding: '2px 10px' }}
              onClick={() => {
                setShowCreateConditionalTemplateQuestionSetModal(true);
              }}
            >
              + Create New Question Set
            </Button>
          </div>
          <Grid container spacing={2}>
            <Grid item md={12} xs={12}>
              <ServerEnabledDataGrid<ConditionalTemplateQuestionSet_ConditionalTemplateQuestionSetDataGridPageFilter>
                loading={
                  getAllConditionalTemplateQuestionSetsForConditionalTemplateQuestionSetDataGridLazyQueryResult.loading
                }
                className={classes.root}
                columns={columns}
                gridStateKey={GridStateKey.ConditionalTemplateQuestionSetGrid}
                queryResult={
                  getAllConditionalTemplateQuestionSetsForConditionalTemplateQuestionSetDataGridLazyQueryResult
                }
                executeLazyQuery={
                  getAllConditionalTemplateQuestionSetsForConditionalTemplateQuestionSetDataGridLazyQuery
                }
                disableColumnReorder
                disableRowSelectionOnClick
                autoHeight={true}
                rowHeight={65}
                pagination
                initialState={{
                  pagination: { paginationModel: { pageSize: 100 } },
                }}
              />
            </Grid>
          </Grid>
        </div>
      </>
    </HomeLayout>
  );
}
